import { NotificationT } from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import { shallowEqual } from 'react-redux';

import { appActions } from 'app/app.actions';
import { actions as conversationActions } from 'components/conversation/actions/conversation';
import { getCsidFromAcwNotificationId } from 'components/conversation/utils/acwNotification';
import { notificationActions } from 'components/notifications/actions/notification';
import { actions as personalActions } from 'components/settings/actions/personal';
import { composeEmailActions } from 'components/shared/composeEmail/actions/composeEmailActions';
import { actions } from 'components/telephony/actions/telephony';
import { createCommands, useCommandHandler } from 'utils/commands';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useAppNavigate } from 'utils/url/useAppNavigate';

const { telephonySetMute, telephonySetHold } = actions;

const {
  conversationRespondeToTransferOfferAccepted,
  conversationRespondeToOfferAccepted,
  conversationRespondeToTransferOfferRejected,
  conversationRespondeToOfferRejected,
  conversationClose,
  conversationExtendAcw,
  conversationCompleteAcw,
} = conversationActions;

const { notificationRemoveByType } = notificationActions;

const { composeEmailOpenModal } = composeEmailActions;

export const conversationCommands = createCommands([
  {
    group: 'conversation',
    label: 'Offer: accept',
    name: 'acceptOffer',
    description: 'Accepts a conversation offer',
    keyboard: {
      keybindings: [{ binding: [['$metaOrCtrl', 'Alt'], 'KeyA'] }],
    },
    useRegisterHandler: () => {
      const dispatch = useAppDispatch();
      const conversationOffers = useAppSelector(
        (state) => state.offers.conversationOffers,
      );

      useCommandHandler({
        name: 'conversation:acceptOffer',
        actionFn: () => {
          const conversationOffer = conversationOffers?.[0];
          const hasOffers = conversationOffers?.length > 0;
          const canAcceptOffer = hasOffers && !!conversationOffer?.csid;

          if (!canAcceptOffer) {
            return;
          }

          const { csid, isInternal, transferType } = conversationOffer;

          const action = transferType
            ? conversationRespondeToTransferOfferAccepted
            : conversationRespondeToOfferAccepted;

          dispatch(
            action({
              csid,
              did_accept: true,
              isInternal,
            }),
          );

          dispatch(
            notificationRemoveByType({ type: `conversationOffer_${csid}` }),
          );
        },
      });
    },
  },
  {
    group: 'conversation',
    label: 'Offer: reject',
    name: 'rejectOffer',
    description: 'Reject a conversation offer',
    keyboard: {
      keybindings: [{ binding: [['$metaOrCtrl', '$controlOrAlt'], 'KeyR'] }],
    },
    useRegisterHandler: () => {
      const dispatch = useAppDispatch();
      const conversationOffers = useAppSelector(
        (state) => state.offers.conversationOffers,
      );

      useCommandHandler({
        name: 'conversation:rejectOffer',
        actionFn: () => {
          const conversationOffer = conversationOffers?.[0];
          const hasOffers = conversationOffers?.length > 0;
          const canRejectOffer = hasOffers && !!conversationOffer?.csid;

          if (!canRejectOffer) {
            return;
          }

          const { csid, isInternal, transferType } = conversationOffer;

          const action = transferType
            ? conversationRespondeToTransferOfferRejected
            : conversationRespondeToOfferRejected;

          dispatch(
            action({
              csid,
              did_accept: false,
              isInternal,
            }),
          );

          dispatch(
            notificationRemoveByType({ type: `conversationOffer_${csid}` }),
          );
        },
      });
    },
  },
  {
    group: 'conversation',
    label: 'Compose new email',
    name: 'composeNewEmail',
    description: 'Opens composing a new email',
    keyboard: {
      keybindings: [{ binding: [['$metaOrCtrl', '$controlOrAlt'], 'KeyE'] }],
    },
    useRegisterHandler: () => {
      const dispatch = useAppDispatch();

      useCommandHandler({
        name: 'conversation:composeNewEmail',
        actionFn: () => {
          dispatch(composeEmailOpenModal());
        },
      });
    },
  },
  {
    group: 'conversation',
    label: 'Compose new SMS',
    name: 'composeNewSms',
    description: 'Opens composing a new SMS',
    featureFlag: 'sms-compose',
    keyboard: {
      keybindings: [{ binding: [['$metaOrCtrl', '$controlOrAlt'], 'KeyS'] }],
    },
    useRegisterHandler: () => {
      const dispatch = useAppDispatch();

      useCommandHandler({
        name: 'conversation:composeNewSms',
        actionFn: () => {
          dispatch(
            appActions.showModal({
              modal: { type: 'app:composeSms' },
            }),
          );
        },
      });
    },
  },
  {
    group: 'conversation',
    label: 'Toggle focus mode',
    name: 'toggleConversationFocusMode',
    description: '',
    keyboard: {
      keybindings: [{ binding: [['$controlOrAlt'], 'IntlBackslash'] }],
    },
  },
  {
    group: 'conversation',
    label: 'Hold current call',
    name: 'phoneToggleHold',
    description: 'Toggles hold of the current call',
    keyboard: {
      keybindings: [{ binding: [['$metaOrCtrl', '$controlOrAlt'], 'KeyH'] }],
    },
    useRegisterHandler: () => {
      const dispatch = useAppDispatch();
      const {
        telephony: { currentCall, isOnHold },
        metaConversation: {
          conversation: {
            id: csid,
            initial_channel: initialChannel,
            is_closed: isClosed,
          },
          isAssignedToMe,
          requester,
        },
      } = useAppSelector(
        (state) => ({
          telephony: state.telephony,
          metaConversation: state.conversation.metaConversation,
        }),
        shallowEqual,
      );

      useCommandHandler({
        name: 'conversation:phoneToggleHold',
        actionFn: () => {
          const isTelephonyChannel =
            initialChannel === 'pstnphone' || initialChannel === 'callback';
          const isActivePhoneConversation =
            isClosed === false && isAssignedToMe === true && isTelephonyChannel;

          const isCurrentCall = currentCall !== null;
          const canToggleHold = isActivePhoneConversation && isCurrentCall;

          if (!canToggleHold) {
            return;
          }

          dispatch(
            telephonySetHold({
              setTo: !isOnHold,
              csid,
              requesterId: requester?.id,
            }),
          );
        },
      });
    },
  },
  {
    group: 'conversation',
    label: 'Mute current call',
    name: 'phoneToggleMute',
    description: 'Toggles voice of the current call',
    keyboard: {
      keybindings_mac: [{ binding: [['Meta', 'Alt'], 'KeyM'] }],
      keybindings_pc: [{ binding: [['Control', 'Alt'], 'KeyD'] }],
    },
    useRegisterHandler: () => {
      const dispatch = useAppDispatch();
      const {
        telephony: { currentCall, isOnMute },
        metaConversation: {
          conversation: {
            id: csid,
            initial_channel: initialChannel,
            is_closed: isClosed,
          },
          isAssignedToMe,
        },
      } = useAppSelector(
        (state) => ({
          telephony: state.telephony,
          metaConversation: state.conversation.metaConversation,
        }),
        shallowEqual,
      );

      useCommandHandler({
        name: 'conversation:phoneToggleMute',
        actionFn: () => {
          const isTelephonyChannel =
            initialChannel === 'pstnphone' || initialChannel === 'callback';
          const isActivePhoneConversation =
            isClosed === false && isAssignedToMe === true && isTelephonyChannel;

          const isCurrentCall = currentCall !== null;
          const canToggleMute = isActivePhoneConversation && isCurrentCall;

          if (!canToggleMute) {
            return;
          }

          dispatch(
            telephonySetMute({
              setTo: !isOnMute,
              csid,
            }),
          );
        },
      });
    },
  },
  {
    group: 'conversation',
    label: 'Wrap-up: extend all',
    name: 'extendWrapUp',
    description: 'Extends all currently open wrap-up times',
    keyboard: {
      keybindings: [{ binding: [[], 'KeyE'] }],
    },
    useRegisterHandler: () => {
      const dispatch = useAppDispatch();
      const wrapUpNotifications = useAppSelector((state) => {
        return state.notifications.currentNotifications.filter(
          ({ type }) => type === 'acw',
        );
      });

      useCommandHandler({
        name: 'conversation:extendWrapUp',
        actionFn: () => {
          wrapUpNotifications.forEach(({ id, shouldBeRemovedAt }) => {
            const csid = getCsidFromAcwNotificationId(id);

            if (csid && shouldBeRemovedAt) {
              const newDuration =
                30 +
                Math.round((shouldBeRemovedAt - new Date().getTime()) / 1000);
              dispatch(conversationExtendAcw({ csid, duration: newDuration }));
            }
          });
        },
      });
    },
  },
  {
    group: 'conversation',
    label: 'Wrap-up: end latest',
    name: 'endWrapUp',
    description: 'Ends latest wrap-up time',
    keyboard: {
      keybindings: [{ binding: [['Shift', '$metaOrCtrl'], 'KeyE'] }],
    },
    useRegisterHandler: () => {
      const dispatch = useAppDispatch();
      const latestWrapUpNotification: NotificationT | undefined =
        useAppSelector((state) => {
          return state.notifications.currentNotifications
            .filter(({ type }) => type === 'acw')
            .slice(-1)[0];
        });

      useCommandHandler({
        name: 'conversation:endWrapUp',
        actionFn: () => {
          if (!latestWrapUpNotification) {
            return;
          }

          const csid = getCsidFromAcwNotificationId(
            latestWrapUpNotification.id,
          );

          if (csid && latestWrapUpNotification.shouldBeRemovedAt) {
            dispatch(conversationCompleteAcw({ csid }));
          }
        },
      });
    },
  },
  {
    group: 'conversation',
    label: 'Go to conversation',
    name: 'goToConversation',
    keyboard: {
      keybindings: [{ binding: [['$metaOrCtrl', '$controlOrAlt'], 'KeyO'] }],
    },
    useRegisterHandler: () => {
      const navigate = useAppNavigate();
      const latestMessageSentNotification: NotificationT | undefined =
        useAppSelector((state) => {
          return state.notifications.currentNotifications
            .filter(({ type }) => type === 'messageSent')
            .slice(-1)[0];
        });

      useCommandHandler({
        name: 'conversation:goToConversation',
        actionFn: () => {
          if (!latestMessageSentNotification) {
            return;
          }

          const csid = parseInt(
            latestMessageSentNotification.id.replace('messageSent_', ''),
          );

          if (isNaN(csid)) {
            return;
          }

          navigate({
            path: '/conversation/:csid',
            params: { path: { csid: csid.toString() } },
          });
        },
      });
    },
  },
  {
    group: 'conversation',
    label: 'Claim conversation',
    name: 'claim',
    description: 'Claims currently viewed conversation',
    keyboard: {
      keybindings: [{ binding: [['$metaOrCtrl', '$controlOrAlt'], 'KeyC'] }],
    },
  },
  {
    group: 'conversation',
    label: 'Follow-up',
    name: 'followUp',
    description: 'Opens follow-up selector for selecting a follow-up',
    keyboard: {
      keybindings: [{ binding: [['$metaOrCtrl', 'Alt'], 'KeyF'] }],
    },
  },
  {
    group: 'conversation',
    label: 'Toggle internal note / reply view',
    name: 'internalNote',
    description: 'Toggles between reply and internal note view',
    keyboard: {
      keybindings: [{ binding: [['$metaOrCtrl', '$controlOrAlt'], 'KeyX'] }],
    },
  },
  {
    group: 'conversation',
    label: 'Reply',
    name: 'reply',
    description: 'Opens the reply editor',
    keyboard: {
      keybindings: [{ binding: [['$metaOrCtrl', '$controlOrAlt'], 'KeyP'] }],
    },
  },
  {
    group: 'conversation',
    label: 'Tags selector',
    name: 'tagsSelector',
    description: 'Opens tags selector for quick tag add/removal',
    keyboard: {
      keybindings: [{ binding: [['$metaOrCtrl', 'Alt'], 'KeyY'] }],
    },
  },
  {
    group: 'conversation',
    label: 'Close/end conversation',
    name: 'close',
    description: 'Closes/ends currently viewed conversation',
    keyboard: {
      keybindings: [{ binding: [['$metaOrCtrl', '$controlOrAlt'], 'KeyW'] }],
    },
    useRegisterHandler: () => {
      const dispatch = useAppDispatch();
      const {
        metaConversation: {
          conversation: {
            id: csid,
            is_closed: isClosed,
            initial_channel: initialChannel,
            attributes,
            direction,
            tags,
          },
          isAssignedToMe,
        },
      } = useAppSelector(
        (state) => ({
          telephony: state.telephony,
          metaConversation: state.conversation.metaConversation,
        }),
        shallowEqual,
      );

      useCommandHandler({
        name: 'conversation:close',
        actionFn: () => {
          const canClose = csid !== null && !isClosed && isAssignedToMe;

          if (!canClose) {
            return;
          }

          dispatch(
            conversationClose({
              csid,
            }),
          );

          ampli.conversationClosed({
            location: 'command',
            channel: initialChannel || undefined,
            language: attributes?.language,
            direction: direction || undefined,
            tags: tags,
          });
        },
      });
    },
  },
  {
    group: 'conversation',
    label: 'Toggle activity log / conversation view',
    name: 'activityLog',
    keyboard: {
      keybindings: [{ binding: [['$metaOrCtrl', '$controlOrAlt'], 'KeyL'] }],
    },
  },
  {
    group: 'conversation',
    label: 'Transfer to queue',
    name: 'transferToQueue',
    keyboard: {
      keybindings: [{ binding: [['$metaOrCtrl', '$controlOrAlt'], 'KeyT'] }],
    },
  },
  {
    group: 'conversation',
    label: 'Transfer to agent',
    name: 'transferToAgent',
    keyboard: {
      keybindings: [{ binding: [['$metaOrCtrl', '$controlOrAlt'], 'KeyG'] }],
    },
  },
  {
    group: 'conversation',
    label: 'Transfer to number',
    name: 'transferToNumber',
    keyboard: {
      keybindings: [
        { binding: [['$metaOrCtrl', 'Shift', '$controlOrAlt'], 'KeyN'] },
      ],
    },
  },
  {
    group: 'conversation',
    label: 'Messages: scroll to top',
    name: 'messagesScrollToTop',
    keyboard: {
      keybindings: [{ binding: [['$metaOrCtrl', '$controlOrAlt'], 'ArrowUp'] }],
    },
  },
  {
    group: 'conversation',
    label: 'Messages: scroll to bottom',
    name: 'messagesScrollToBottom',
    keyboard: {
      keybindings: [
        { binding: [['$metaOrCtrl', '$controlOrAlt'], 'ArrowDown'] },
      ],
    },
  },
  {
    group: 'conversation',
    label: 'Conversation details',
    name: 'details',
    keyboard: {
      keybindings: [{ binding: [['$metaOrCtrl', '$controlOrAlt'], 'KeyI'] }],
    },
  },
  {
    group: 'conversation',
    label: 'Edit subject',
    name: 'editSubject',
    description: 'Edits the conversation subject',
    keyboard: {
      keybindings: [{ binding: [['$metaOrCtrl', '$controlOrAlt'], 'KeyJ'] }],
    },
    featureFlag: 'conversation-edit-subject',
  },
  {
    group: 'conversation',
    label: 'Change CSAT',
    name: 'csat',
    description: 'Opens CSAT language selector',
  },
  {
    group: 'conversation',
    label: 'Toggle resolution',
    name: 'resolve',
    description: 'Toggles the conversation resolution status',
    featureFlag: 'conversation-resolution-state',
  },
  {
    group: 'conversation',
    label: 'Go to active conversation #1',
    name: 'goToActiveConversation1',
  },
  {
    group: 'conversation',
    label: 'Go to active conversation #2',
    name: 'goToActiveConversation2',
  },
  {
    group: 'conversation',
    label: 'Go to active conversation #3',
    name: 'goToActiveConversation3',
  },
  {
    group: 'conversation',
    label: 'Go to active conversation #4',
    name: 'goToActiveConversation4',
  },
  {
    group: 'conversation',
    label: 'Go to active conversation #5',
    name: 'goToActiveConversation5',
  },
  {
    group: 'conversation',
    label: 'Go to active conversation #6',
    name: 'goToActiveConversation6',
  },
  {
    group: 'conversation',
    label: 'Go to active conversation #7',
    name: 'goToActiveConversation7',
  },
  {
    group: 'conversation',
    label: 'Go to active conversation #8',
    name: 'goToActiveConversation8',
  },
  {
    group: 'conversation',
    label: 'Go to active conversation #9',
    name: 'goToActiveConversation9',
  },
  {
    group: 'conversation',
    label: 'Toggle right sidebar',
    name: 'toggleRightSidebar',
    description: 'Shows or hides the customer info sidebar',
    keyboard: {
      keybindings: [{ binding: [[], 'BracketRight'] }],
    },
    useRegisterHandler: () => {
      const dispatch = useAppDispatch();
      const isCustomCardMenuOpen = useAppSelector(
        (state) => state.settings.personal.isCustomCardMenuOpen,
      );

      useCommandHandler({
        name: 'conversation:toggleRightSidebar',
        actionFn: () => {
          dispatch(
            personalActions.personalCustomCardMenu({
              key: 'isCustomCardMenuOpen',
              value: !isCustomCardMenuOpen,
            }),
          );
        },
      });
    },
  },
]);
